import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
	Text,
	Loader,
	Spacer,
	StatusChip
} from '@components';
import {
	TEXT_WEIGHT,
	CHIP_TYPE,
	CHIP_SIZE,
	TFinanceAuditEvent,
	FINANCE_AUDIT_SEVERITY
} from '@types';
import { useGetShipmentFinanceAudit, useGetInvoiceFinanceAudit } from '@api';
import { formatDate } from '@utils';
import styles from './AuditLog.module.scss';

/*
	What finance did to this document, oldest first.

	finance-api-v2 records every step it takes — invoice created, synced, emailed or
	suppressed and why, deferred pending POD verification, paid, cancelled — and EOS
	proxies that back. Grouped by day because the useful question is almost always
	"what happened around the time it went wrong", which an undivided list of forty
	rows does not answer.
*/

export interface AuditLogProps {
	/* Exactly one of these. The shipment id is EOS's own; the invoice reference is the
	   external one finance knows it by. */
	shipmentId?: number | string;
	invoiceReference?: string;
}

const SEVERITY_CLASS: Record<string, string> = {
	[FINANCE_AUDIT_SEVERITY.ERROR]: styles.error,
	[FINANCE_AUDIT_SEVERITY.WARNING]: styles.warning,
	[FINANCE_AUDIT_SEVERITY.INFO]: styles.info
};

/*
	Events submitted by EOS, MINT and NOVA are all stored under the single action
	`external.event_recorded`, with the action they actually describe kept in context.
	Reading the stored action directly makes every forwarded milestone — POD uploaded,
	accepted, rejected, claim raised, payment held — render the same headline, which is
	what made the trail look full of duplicates.
*/
const actionOf = (event: TFinanceAuditEvent): string =>
	typeof event.context?.externalAction === 'string' && event.context.externalAction !== ''
		? event.context.externalAction
		: event.action;

/*
	The other source of look-alike rows: changes that genuinely happened twice, once per
	ledger, and acceptances that could have come from any of several automatic routes.
	Those rows differ only in context, so without this the reader sees two identical
	lines and no way to tell them apart.
*/
const DISTINGUISHING_CONTEXT = ['ledger', 'mechanism', 'shipmentStatus'] as const;

const contextTags = (context: TFinanceAuditEvent['context']): string[] =>
	DISTINGUISHING_CONTEXT
		.map((key) => context?.[key])
		.filter((value): value is string => typeof value === 'string' && value !== '');


/*
	Actions read as `domain.past_tense_verb`. The document type leads the headline as
	plain text rather than sitting beside it in a chip — "Purchase Order Created" is a
	sentence a reader scans, "Created" plus a chip is two things to assemble.
*/
const headlineOf = (action: string): string => {
	const [domain = '', verb = ''] = String(action).split('.');
	const documentType = domain
		.split('_')
		.filter(Boolean)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
	const words = verb.replace(/_/g, ' ');
	const event = words.charAt(0).toUpperCase() + words.slice(1);

	return [documentType, event].filter(Boolean).join(' ');
}

/*
	The invoice this event was really about, when the reader is looking at something else.
	A shipment's row saying "Invoice Created" names a document it otherwise cannot reach.
	Netsuite references are prefixed on the detail route, the same way the invoice list
	links to them.
*/
const invoiceLinkOf = (event: TFinanceAuditEvent): { reference: string; url: string } | null => {
	const invoice = event.subjects?.find((subject) => subject.type === 'invoice');

	if (!invoice) return null;

	const isNetsuite = event.context?.ledger === 'netsuite';

	return {
		reference: invoice.reference,
		url: `/finance/invoice_management/detail/${isNetsuite ? 'ns-' : ''}${invoice.reference}`
	};
}

/* A `system` actor is not missing data — scheduled and queue-driven work genuinely has
   nobody behind it, and saying so is more honest than an empty cell. */
const actorLabel = (actor: TFinanceAuditEvent['actor']): string => {
	if (!actor) return 'System';
	if (actor.label) return actor.label;
	if (actor.id) return actor.id;

	return actor.type === 'user' ? 'A user' : 'System';
}

const dayOf = (timestamp: string): string => formatDate(new Date(timestamp), 'FULL_DATE_WITH_DAY');

const timeOf = (timestamp: string): string => {
	const date = new Date(timestamp);
	if (isNaN(date.getTime())) return '';

	return `${`${date.getHours()}`.padStart(2, '0')}:${`${date.getMinutes()}`.padStart(2, '0')}`;
}

/*
	Everything a reader can actually see on a row. Two rows with the same signature are
	indistinguishable on screen, whatever separates them in the database.
*/
const signatureOf = (event: TFinanceAuditEvent): string => [
	actionOf(event),
	event.severity,
	event.summary ?? '',
	actorLabel(event.actor),
	event.sourceSystem,
	event.state?.from ?? '',
	event.state?.to ?? '',
	event.error?.class ?? '',
	invoiceLinkOf(event)?.url ?? '',
	contextTags(event.context).join('|')
].join('\u0000');

/*
	A shipment is published as verified once per message, and the same verification can
	arrive on several messages — finance keys its dedupe on the message, so each is kept.
	They are real rows and must not be dropped, but three identical lines in a row tell
	the reader nothing three times. Runs of them collapse into one with a count; anything
	that differs in a visible field stays on its own line.
*/
type TGroupedEvent = { event: TFinanceAuditEvent; count: number };

const collapseRuns = (events: TFinanceAuditEvent[]): TGroupedEvent[] =>
	events.reduce<TGroupedEvent[]>((runs, event) => {
		const previous = runs[runs.length - 1];

		if (previous && signatureOf(previous.event) === signatureOf(event)) {
			previous.count += 1;

			return runs;
		}

		return [...runs, { event, count: 1 }];
	}, []);

export const AuditLog = (props: AuditLogProps) => {
	const { shipmentId, invoiceReference } = props;
	const [events, setEvents] = useState<TFinanceAuditEvent[]>([]);
	const [loading, setLoading] = useState(true);
	const [failed, setFailed] = useState(false);
	const { callGetShipmentFinanceAudit } = useGetShipmentFinanceAudit();
	const { callGetInvoiceFinanceAudit } = useGetInvoiceFinanceAudit();

	useEffect(() => {
		if (!shipmentId && !invoiceReference) return;

		const controller = new AbortController();
		let cancelled = false;

		setLoading(true);
		setFailed(false);

		const call = invoiceReference
			? callGetInvoiceFinanceAudit({
				routes: [invoiceReference],
				signal: controller.signal
			})
			: callGetShipmentFinanceAudit({
				routes: [`${shipmentId}`],
				signal: controller.signal
			});

		call.then((res) => {
			if (cancelled) return;
			setEvents(res?.data?.data ?? []);
		}).catch(() => {
			/* An empty list and a failed fetch must not look the same here. The whole
			   value of the trail is that absence is meaningful. */
			if (!cancelled) setFailed(true);
		}).finally(() => {
			if (!cancelled) setLoading(false);
		});

		return () => {
			cancelled = true;
			controller.abort();
		}
	}, [shipmentId, invoiceReference]);

	if (loading) return <Loader />;

	if (failed) {
		return <div className={styles.empty}>
			<Text>Couldn&apos;t load the audit log. Please try again.</Text>
		</div>;
	}

	if (!events.length) {
		return <div className={styles.empty}>
			<Text>Nothing has been recorded against this record yet.</Text>
		</div>;
	}

	const days = events.reduce<Record<string, TFinanceAuditEvent[]>>((groups, event) => {
		const day = dayOf(event.occurredAt);
		(groups[day] = groups[day] ?? []).push(event);

		return groups;
	}, {});

	return <div className={styles.trail}>

		{Object.entries(days).map(([day, dayEvents]) => <section key={day} className={styles.day}>
			<Text weight={TEXT_WEIGHT.SEMI_BOLD} color={'var(--neutral-07)'}>{day}</Text>
			<Spacer size={12} />

			{collapseRuns(dayEvents).map(({ event, count }) => {
				const invoiceLink = invoiceLinkOf(event);

				return <article key={event.id} className={styles.event}>
					<div className={styles.time}><Text>{timeOf(event.occurredAt)}</Text></div>

					<div className={`${styles.marker} ${SEVERITY_CLASS[event.severity] ?? ''}`} />

					<div className={styles.body}>
						<div className={styles.headline}>
							<Text weight={TEXT_WEIGHT.SEMI_BOLD}>{headlineOf(actionOf(event))}</Text>
							{count > 1 && <StatusChip
								text={`×${count}`}
								type={CHIP_TYPE.NEUTRAL}
								size={CHIP_SIZE.SMALL}
								title={`Recorded ${count} times`}
							/>}
						</div>

						{event.summary && <Text color={'var(--neutral-07)'}>{event.summary}</Text>}

						{/* The invoice this row is really about, rather than a badge saying
					    the row is about something else without naming it. */}
						{invoiceLink && <Link className={styles.related} to={invoiceLink.url}>
						Invoice {invoiceLink.reference}
						</Link>}

						<div className={styles.meta}>
							<span>{actorLabel(event.actor)}</span>
							<span>via {event.sourceSystem}</span>
							{event.state?.to && <span>
								{event.state.from ? `${event.state.from} → ` : ''}{event.state.to}
							</span>}
						</div>

						{contextTags(event.context).length > 0 && <div className={styles.tags}>
							{contextTags(event.context).map((tag) => <StatusChip
								key={tag}
								text={tag}
								type={CHIP_TYPE.NEUTRAL}
								size={CHIP_SIZE.SMALL}
							/>)}
						</div>}

						{event.error && <div className={styles.errorDetail}>
							{event.error.class}{event.error.message ? `: ${event.error.message}` : ''}
						</div>}

						{/* Recorded on a different day than it happened means a backdated or
					    replayed event, which is usually the thing someone is chasing. */}
						{event.recordedAt && event.occurredAt.slice(0, 10) !== event.recordedAt.slice(0, 10) &&
						<div className={styles.recorded}>Recorded {dayOf(event.recordedAt)}</div>
						}
					</div>
				</article>;
			})}
		</section>)}
	</div>;
}
